import type {
  DrawingDocument,
  DrawingEntity,
  DrawingSpace,
} from "@dxf-ai-editor/drawing-model";

import {
  peekDocumentMemoryHolder,
  snapshotSpaceEntities,
} from "./mutationStore";

/**
 * Phase 4 Wave 1 Task B — read-through wrapper for `DrawingSpace`.
 *
 * Currently `DrawingDocument.spaces[i].entities` is a plain array
 * stored alongside the compact cache — duplicate storage. The
 * compact-cache id-index is the source of truth for huge drawings;
 * `space.entities` is kept populated for small/legacy paths but
 * effectively shadows the cache.
 *
 * This wrapper hides that duplication at the dispatch boundary. When
 * `wrapSpaceAsReadThrough(space, docId)` is called, the returned object
 * has the same shape as `space` *except* that its `entities` property
 * is materialised on every access from the active document's compact
 * cache via `snapshotSpaceEntities(docId, space.id)`. Existing
 * consumers continue to write `space.entities.length` / `space.entities.map(…)`
 * and see live values from the cache.
 *
 * **Why opt-in at the dispatch boundary (not a schema-level Proxy):**
 *  - `drawingSpaceSchema` (Zod) defines `entities: z.array(...)` as a
 *    plain array; downstream type-aware consumers (reducers, exporters,
 *    tests) hold `DrawingSpace` literals with concrete `.entities: DrawingEntity[]`.
 *    A schema-level wrapper would have to mutate that contract on every
 *    `drawingSpaceSchema.parse` call.
 *  - Tests build `DrawingSpace` literals as plain objects; those need
 *    to keep working unchanged because they don't have a compact cache
 *    backing them.
 *  - The dispatch boundary (`editorStore.dispatch`,
 *    `mergeImportedGeometryDocument`) is the natural place to wrap the
 *    document once after each reducer pass — every downstream React
 *    consumer sees the wrapped form.
 *
 * **Identity & recursion safety:**
 *  - `Object.defineProperty(wrapper, "entities", { get })` overlays the
 *    getter on a shallow clone of `space`. `JSON.stringify(wrapper)`
 *    invokes the getter once, returns the materialised array, and
 *    serialises it — no recursion.
 *  - The wrapper does not retain a reference to the snapshot result,
 *    so each access re-snapshots (consistent with `getEntityFromCompactCache`'s
 *    "freshly constructed" contract).
 *
 * **Fallback behaviour (Phase 4 Wave 1 codex P2):**
 *  - The getter falls back to the original `space.entities` array iff
 *    the doc's holder has never been written through the cache path
 *    (`holder === undefined` or `cachePopulatedOnce === false`). The
 *    sticky `cachePopulatedOnce` flag — set on first
 *    `populateCompactCache` / `appendToCompactCache` and cleared only
 *    by `clearCompactCache` / `clearMutationStore` — distinguishes a
 *    legacy small-drawing path (inline `space.entities` is the source
 *    of truth) from a legitimately-drained cache (`idIndex.size === 0`
 *    after removes; snapshot's `[]` is the source of truth).
 *  - F1 cycle-1's "gate on holder existence" was wrong because
 *    `clearMutationStore()` and friends eagerly mint an empty default
 *    holder, which would mask the small-drawing fallback.
 */

/**
 * Phase 4 Wave 1 Task B — wrap a single `DrawingSpace` so its
 * `entities` property is a live read-through into the compact cache.
 *
 * The returned object has the same static type as the input — readers
 * can keep destructuring `entities`, calling `.map`, `.filter` etc.
 *
 * @param space  Original space object — its own `.entities` array is
 *               retained as a fallback (see "Fallback behaviour"
 *               above).
 * @param docId  Document id passed to `snapshotSpaceEntities`. Caller
 *               (editorStore) supplies the current
 *               `getActiveDocumentId()`.
 */
export function wrapSpaceAsReadThrough(
  space: DrawingSpace,
  docId: string,
): DrawingSpace {
  // Shallow-clone first so attaching the getter doesn't mutate the
  // input (some consumers compare doc references for identity).
  //
  // PR #393 @codex Finding 3 (LAZY WRAP) — copy the space's own property
  // DESCRIPTORS verbatim instead of spreading (`{ ...space }`). When `space`
  // is ITSELF a read-through wrapper (the common case on every in-place dispatch
  // — the reducer returns the SAME read-through `spaces`, and the assemble
  // in-place path passes them through untouched), spreading INVOKES its
  // `entities` accessor, re-materializing the WHOLE compact store on EVERY
  // dispatch — moving the O(N) snapshot from the reducer (which T6d-2 just
  // eliminated) to this wrap seam. `Object.getOwnPropertyDescriptors` reads the
  // accessor DESCRIPTOR (the get/set functions) WITHOUT invoking it, and
  // `Object.defineProperties` re-installs it on the clone unchanged — so the
  // copy stays fully lazy. The `entities` descriptor copied here is then
  // OVERWRITTEN below by the fresh read-through getter (configurable: true),
  // so the source wrapper's getter is never read; copying it is harmless and
  // keeps this one expression the single clone site.
  const wrapper = Object.defineProperties(
    {},
    Object.getOwnPropertyDescriptors(space),
  ) as DrawingSpace;

  // Phase 4 Wave 2 (П35Р follow-up) — per-wrapper memoised snapshot.
  // The previous revision called `snapshotSpaceEntities(docId, space.id)`
  // on EVERY access, which iterates the full `idIndex` Map (685k entries
  // on П35Р) and allocates 685k DrawingEntity objects each time. React
  // selectors that read `.entities.length` ran this on every render;
  // post-import the main thread spent multi-minute stretches stuck in
  // the wrapper's repeated full-doc materialisation.
  //
  // The memo is keyed by the holder's `cacheVersion` (bumped by every
  // compact-cache write — `populateCompactCache`, `appendToCompactCache`,
  // `removeFromCompactCache`, `setInCompactCache`, `clearCompactCache`,
  // `clearMutationStore`). On a cache miss (version drift OR holder
  // swapped) we re-snapshot once and cache the result for all subsequent
  // accesses at the same version. On a hit we return the cached array —
  // O(1) instead of O(N).
  let cachedSnapshot: DrawingEntity[] | undefined;
  let cachedVersion = -1;
  // Differentiate the cached `cachePopulatedOnce` so we can invalidate
  // when the sticky bit flips (e.g. populate after a clear).
  let cachedPopulatedOnce = false;

  Object.defineProperty(wrapper, "entities", {
    enumerable: true,
    configurable: true,
    get(): DrawingEntity[] {
      // Phase 4 Wave 1 codex P2 — gate fallback on whether the holder
      // has ever taken a write through the cache path, NOT on holder
      // existence or `idIndex.size`. Two cases motivate the sticky bit:
      //
      //  1. F1 invariant — a holder fully drained via removes
      //     (`commitMutation` / `removeFromCompactCache`) has
      //     `idIndex.size === 0`. The snapshot is the source of truth
      //     for this state — wrapper must surface `[]`, not stale
      //     inline entities.
      //
      //  2. P2 invariant — `clearMutationStore()` and similar paths
      //     eagerly mint an empty default holder. Legacy small-drawing
      //     paths that wrote to `space.entities` directly (without
      //     ever calling `populateCompactCache`) would otherwise have
      //     their inline data masked by the snapshot's `[]`.
      //
      // The sticky `cachePopulatedOnce` bit (set on first
      // `populateCompactCache` / `appendToCompactCache`, cleared only
      // by `clearCompactCache` / `clearMutationStore`) discriminates
      // these two cases.
      const holder = peekDocumentMemoryHolder(docId);
      if (holder === undefined || !holder.cachePopulatedOnce) {
        // Bust the memo so a future populate re-snapshots.
        cachedSnapshot = undefined;
        cachedVersion = -1;
        cachedPopulatedOnce = false;
        return space.entities;
      }
      const v = holder.cacheVersion;
      if (
        cachedSnapshot !== undefined &&
        cachedVersion === v &&
        cachedPopulatedOnce === holder.cachePopulatedOnce
      ) {
        return cachedSnapshot;
      }
      const next = snapshotSpaceEntities(docId, space.id);
      cachedSnapshot = next;
      cachedVersion = v;
      cachedPopulatedOnce = holder.cachePopulatedOnce;
      return next;
    },
  });

  return wrapper;
}

/**
 * Phase 4 Wave 1 Task B — wrap every space in a `DrawingDocument` and
 * return a new document object with read-through spaces.
 *
 * Mutates nothing; the input document is left intact.
 */
export function wrapDocumentAsReadThrough(
  document: DrawingDocument,
  docId: string,
): DrawingDocument {
  return {
    ...document,
    spaces: document.spaces.map((space) => wrapSpaceAsReadThrough(space, docId)),
  };
}

/**
 * PR #362 round-5 (F3/F4 ROOT) — FREEZE a (possibly read-through) space into a
 * CONCRETE one: read its `entities` getter EXACTLY ONCE and rebind the result as
 * a plain own-data property (NO getter). The returned object is a normal
 * `DrawingSpace` whose `entities` array is the snapshot taken AT THIS CALL — it
 * will NOT drift when the compact cache is mutated afterwards.
 *
 * Why this exists: `wrapSpaceAsReadThrough` makes `space.entities` a LIVE
 * read-through into the active doc's compact cache. Any site that must DIFF or
 * UNDO-RESTORE the PRE-mutation entity state has to snapshot the array at capture
 * time — otherwise the later read (during the diff, or during the undo restore)
 * fires the getter against the now-POST-mutation cache and sees current, not
 * pre-state, entities. The undo/redo/abort base diff (F3) and the
 * `file/xref-restore-snapshot` capture (F4) both consume this so their
 * pre→post / restore is grounded in a frozen pre-state.
 *
 * `Object.defineProperty(..., { value })` overwrites the wrapper's own
 * accessor descriptor with a plain data property of the SAME enumerable shape,
 * so downstream `space.entities.map(...)` / destructuring keep working and never
 * re-materialise. Mutates nothing on the input — operates on a shallow clone.
 */
export function materializeSpaceConcretely(space: DrawingSpace): DrawingSpace {
  // Read the getter (or the plain array) exactly once.
  const frozenEntities: DrawingEntity[] = [...space.entities];
  const concrete: DrawingSpace = { ...space };
  Object.defineProperty(concrete, "entities", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: frozenEntities,
  });
  return concrete;
}

/**
 * PR #362 round-5 (F3/F4 ROOT) — return a CONCRETE copy of `document` whose every
 * space's `entities` is frozen at this call (see {@link materializeSpaceConcretely}).
 * The non-entity document fields are spread by reference (immutable structural
 * sharing); only the spaces are rebuilt with frozen entity arrays.
 *
 * Used to capture a stable PRE-mutation base for the undo/redo/abort net diff
 * and for the xref undo snapshot — both must reflect the document state AT
 * CAPTURE TIME, not whatever the live cache holds when the diff/restore later
 * reads `.entities`.
 */
export function materializeDocumentSpacesConcretely(
  document: DrawingDocument,
): DrawingDocument {
  return {
    ...document,
    spaces: document.spaces.map((space) => materializeSpaceConcretely(space)),
  };
}
